import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useClient } from 'jsonapi-react';
import { v4 as uuid } from 'uuid';
import { FiBookOpen, FiUsers } from 'react-icons/fi';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { uploadFileFromPresignedUrl } from '@/utils/aws-sdk';

const MAX_VISIBLE_RELATIONS = 2;

// Mesmo padrão do "+X" de taxonomia em ExamRowTeacher (Provas): mostra até 2
// nomes separados por vírgula e, se houver mais, um badge "+X" com o
// restante no hover — em vez do comma-join sem limite do Default
// (Default/StudentCard.jsx), que pode ficar bem longo quando o aluno tem
// vários cursos/turmas.
function buildRelationMeta(label, items, emptyLabel, getName) {
  if (items.length === 0) return { text: `${label}: ${emptyLabel}` };

  const visible = items.slice(0, MAX_VISIBLE_RELATIONS);
  const extra = items.slice(MAX_VISIBLE_RELATIONS);

  return {
    text: `${label}: ${visible.map(getName).join(', ')}`,
    extraCount: extra.length,
    extraTitle: extra.map(getName).join(', ')
  };
}

// Mesma regra de Default/StudentCard.jsx: "Realizado online" é
// question-books-user presente com answer-sheet === null; os demais estados
// vêm de answer-sheet['processing-status'].
export function getStudentStatus(student) {
  const questionBooksUser = student?.['question-books-user'];
  if (!questionBooksUser) return null;

  const answerSheet = questionBooksUser['answer-sheet'];
  if (answerSheet === null) return 'online';

  return answerSheet?.['processing-status'] ?? null;
}

function getStatusMeta(status) {
  switch (status) {
    case 'processing':
      return { text: 'Processando', variant: 'amber-soft' };
    case 'completed':
      return { text: 'Concluído', variant: 'success-soft' };
    case 'failed':
      return { text: 'Erro ao processar', variant: 'danger-soft' };
    case 'online':
      return { text: 'Realizado online', variant: 'success-soft' };
    default:
      return { text: 'Ainda não realizada', variant: 'muted' };
  }
}

// Mesmo shell de card usado em ExamRowTeacher/ExamResultRowTeacher (Provas e
// Resultados), pra manter as 3 telas playful de avaliações visualmente
// consistentes. Envio de cartão-resposta reaproveita o mesmo fluxo real do
// Default (Default/StudentCard.jsx): presign S3 -> upload -> cria o
// answer_sheet.
export function OfflineStudentRowTeacher({ student, courses = [], classrooms = [], examId, index = 0, onSent }) {
  const client = useClient();
  const fileInputRef = useRef(null);
  const [sending, setSending] = useState(false);

  const status = getStudentStatus(student);
  const statusMeta = getStatusMeta(status);
  const canSend = (status === null || status === 'failed') && !sending;

  const studentCourses = courses.filter(course => student?.['course-ids']?.includes(course.id));
  const studentClassrooms = classrooms.filter(classroom => student?.['classroom-ids']?.includes(classroom.id));

  const meta = [
    // "courses/simple" devolve o nome do curso em "title", não em "name" (ver
    // Default/index.jsx#L253-256, que remapeia o.title -> name pro filtro).
    { icon: FiBookOpen, ...buildRelationMeta('Cursos', studentCourses, 'Nenhum curso', course => course.title) },
    { icon: FiUsers, ...buildRelationMeta('Turmas', studentClassrooms, 'Nenhuma turma', classroom => classroom.title) }
  ];

  const downloadUrl = student?.['question-books-user']?.['answer-sheet']?.url;

  async function handleFileChange(e) {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    setSending(true);
    const toastLoading = toast.loading('Enviando');
    try {
      const filenameParts = file.name.split('.');
      const renamedFile = new File([file], `${uuid().replaceAll('-', '')}.${filenameParts[filenameParts.length - 1]}`, { type: file.type });

      const { data, error } = await client.mutate(`question_books/${examId}/answer_sheets/presign_s3_url`, {
        file_key: `uploads/answer_sheet/${renamedFile.name}`,
        content_type: renamedFile.type
      });

      if (error) {
        toast.error('Erro ao enviar prova');
      } else {
        const uploadResult = await uploadFileFromPresignedUrl(data['presigned-url'], renamedFile);
        if (uploadResult) {
          await client.mutate(`question_books/${examId}/answer_sheets`, {
            url: uploadResult,
            // Mesmo tipo do Default (Default/StudentCard.jsx usa id=parseInt(id)
            // em toda a tela) — examId aqui vem cru de useParams() (string).
            'question-books-user-attributes': { 'user-id': student.id, 'question-book-id': Number(examId) }
          });
          toast.success('Prova enviada');
        } else {
          toast.error('Erro ao enviar prova');
        }
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao enviar prova');
    }
    toast.dismiss(toastLoading);
    setSending(false);
    onSent?.();
  }

  return (
    <div
      className="flex flex-wrap items-center gap-4 rounded-[16px] border border-neutral-100 bg-white p-4 shadow-card transition-shadow duration-150 hover:shadow-card-hover"
      style={{ animation: `trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) ${250 + index * 70}ms both` }}
      data-testid="offline-student-row"
    >
      <Link
        to={`/usuario/metricas/${student.id}`}
        state={{ isRedirected: true }}
        className="flex min-w-[200px] flex-1 items-center gap-3"
      >
        <AvatarWrapper
          src={student?.image?.url}
          text={student.name}
          className="size-9"
        />
        <div>
          <h3
            className="mb-0 text-base font-extrabold leading-snug"
            style={{ color: '#222731' }}
            title={student.name}
          >
            {student.name}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold text-neutral-500">
            {meta.map((item, metaIndex) => (
              <span
                key={metaIndex}
                className="flex items-center gap-1.5"
              >
                <item.icon className="h-3.5 w-3.5 text-mission" />
                {item.text}
                {item.extraCount > 0 && (
                  <Badge
                    variant="muted"
                    size="xs"
                    className="rounded-full px-2 font-bold"
                    title={item.extraTitle}
                  >
                    +{item.extraCount}
                  </Badge>
                )}
              </span>
            ))}
          </div>
        </div>
      </Link>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Badge
          variant={statusMeta.variant}
          size="sm"
        >
          {statusMeta.text}
        </Badge>

        <PlayfulCardButton
          variant="outline"
          className="h-9 w-auto px-4 text-sm"
          disabled={!canSend}
          onClick={() => fileInputRef.current?.click()}
        >
          Enviar prova offline
        </PlayfulCardButton>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
        />

        {downloadUrl ? (
          <a
            href={downloadUrl}
            target="_blank"
            download
            rel="noopener noreferrer"
            className="inline-flex h-9 items-center justify-center rounded-full border border-[var(--color-mission)] bg-white px-4 text-sm font-extrabold text-[var(--color-mission)] transition-transform duration-100 active:translate-y-px"
          >
            Baixar
          </a>
        ) : (
          <PlayfulCardButton
            variant="outline"
            className="h-9 w-auto px-4 text-sm"
            disabled
          >
            Baixar
          </PlayfulCardButton>
        )}
      </div>
    </div>
  );
}
